import { create } from 'zustand';
import type { CaptionEvent, DeviceId, HealthSnapshot, PresencePayload, Snapshot, Telemetry, UpdateStatus } from '@glyph/protocol-ts';
import type { ClientEvent } from '../client/types';

export interface CaptionsState {
  /** Committed lines, newest last (capped at 50). */
  finals: CaptionEvent[];
  /** Current interim text ('' when none). */
  partial: string;
  lastId: number;
}

export interface DeviceLive {
  /** Full device state on connect / 60 s / after settings change. */
  snapshot: Snapshot | null;
  telemetry: Telemetry | null;
  health: HealthSnapshot | null;
  update: UpdateStatus | null;
  captions: CaptionsState;
  presence: PresencePayload | null;
  /** Latest input level in dBFS per channel; up to 25 Hz, coalesced per frame. */
  audioLevel: number[];
  /** Most recent preview JPEG as an object URL (revoked on replace). */
  previewUrl: string | null;
  connected: boolean;
  updatedAt: number;
}

export const emptyLive = (): DeviceLive => ({
  snapshot: null,
  telemetry: null,
  health: null,
  update: null,
  captions: { finals: [], partial: '', lastId: 0 },
  presence: null,
  audioLevel: [-60, -60],
  previewUrl: null,
  connected: false,
  updatedAt: 0,
});

interface LiveStore {
  devices: Record<DeviceId, DeviceLive>;
  _apply: (events: ClientEvent[]) => void;
  reset: (deviceId?: DeviceId) => void;
}

export const liveStore = create<LiveStore>((set, get) => ({
  devices: {},
  _apply: (events) => {
    if (!events.length) return;
    const devices = { ...get().devices };
    const touched = new Set<DeviceId>();
    for (const ev of events) {
      if (ev.topic === '$connection' && ev.deviceId === '*') {
        for (const id of Object.keys(devices)) devices[id] = { ...devices[id]!, connected: ev.data.connected };
        continue;
      }
      const cur = devices[ev.deviceId] ?? emptyLive();
      const next = touched.has(ev.deviceId) ? cur : { ...cur };
      touched.add(ev.deviceId);
      switch (ev.topic) {
        case 'snapshot':
          next.snapshot = ev.data;
          next.health = ev.data.health;
          if (ev.data.telemetry) next.telemetry = ev.data.telemetry;
          if (ev.data.update) next.update = ev.data.update;
          break;
        case 'telemetry':
          next.telemetry = ev.data;
          // a level is only ever current: the subscribe-time replay of a row older than 90 s carries none
          if (ev.data.audio_dbfs?.length && Date.now() - ev.ts < 90_000) next.audioLevel = ev.data.audio_dbfs;
          break;
        case 'levels':
          next.audioLevel = ev.data.dbfs;
          break;
        case 'health':
          next.health = ev.data;
          break;
        case 'presence':
          next.presence = ev.data;
          if (!ev.data.online) next.audioLevel = [-120, -120]; // nothing to meter on a unit that is away
          break;
        case 'update_status':
          next.update = ev.data;
          break;
        case 'caption_event': {
          const c = ev.data;
          if (c.kind === 'clear') next.captions = { finals: [], partial: '', lastId: c.id };
          else if (c.kind === 'partial') next.captions = { ...next.captions, partial: c.text };
          else if (c.id > next.captions.lastId || !next.captions.finals.some((f) => f.id === c.id)) {
            next.captions = { finals: [...next.captions.finals, c].slice(-50), partial: '', lastId: Math.max(next.captions.lastId, c.id) };
          }
          break;
        }
        case '$binary':
          if (ev.data.kind === 'preview' && typeof URL !== 'undefined' && typeof Blob !== 'undefined') {
            if (next.previewUrl) URL.revokeObjectURL(next.previewUrl);
            next.previewUrl = URL.createObjectURL(new Blob([ev.data.jpeg as BlobPart], { type: 'image/jpeg' }));
          }
          break;
        case '$connection':
          next.connected = ev.data.connected;
          break;
        default:
          break;
      }
      next.updatedAt = ev.ts;
      devices[ev.deviceId] = next;
    }
    set({ devices });
  },
  reset: (deviceId) =>
    set((s) => {
      if (!deviceId) return { devices: {} };
      const devices = { ...s.devices };
      delete devices[deviceId];
      return { devices };
    }),
}));

// ---------------------------------------------------------------------------
// requestAnimationFrame coalescing: events are buffered and applied once per frame, so a 25 Hz level
// meter, 4/s partials and telemetry never cause more than one store update per paint.
// ---------------------------------------------------------------------------
let pending: ClientEvent[] = [];
let scheduled = false;
const raf: (cb: () => void) => void = typeof requestAnimationFrame === 'function' ? (cb) => requestAnimationFrame(() => cb()) : (cb) => setTimeout(cb, 16);
const LATEST_ONLY = new Set<string>(['telemetry', 'levels', 'health', 'presence', 'update_status', 'snapshot', '$binary']);

export function pushLiveEvent(ev: ClientEvent) {
  pending.push(ev);
  if (scheduled) return;
  scheduled = true;
  raf(flushLiveEvents);
}

export function flushLiveEvents() {
  scheduled = false;
  const batch = pending;
  pending = [];
  // Keep only the latest telemetry/levels/health/... per device (in first-seen order); keep every caption event.
  const latest = new Map<string, ClientEvent>();
  const ordered: ClientEvent[] = [];
  for (const ev of batch) {
    if (LATEST_ONLY.has(ev.topic)) {
      const k = `${ev.deviceId}|${ev.topic}`;
      if (!latest.has(k)) ordered.push(ev);
      latest.set(k, ev);
    } else ordered.push(ev);
  }
  liveStore.getState()._apply(ordered.map((ev) => (LATEST_ONLY.has(ev.topic) ? latest.get(`${ev.deviceId}|${ev.topic}`)! : ev)));
}

const EMPTY = emptyLive();
export const selectLive = (deviceId: DeviceId) => (s: LiveStore) => s.devices[deviceId] ?? EMPTY;
